import { z } from "zod";

/**
 * Where a client file import's data came from (#749). Asked on New Import,
 * stored on the list it creates, shown to Invyo in Collection.
 *
 * One key and one label per category and per tool, for the import form, the
 * API's validation and the Collection column alike. The order of each list IS
 * the order the form shows.
 */
export const DATA_SOURCE_CATEGORIES = ["crm", "sales_prospecting_tool", "invyo_extension", "other"] as const;
export type DataSourceCategory = (typeof DATA_SOURCE_CATEGORIES)[number];

/** Set on a client's list created from the extension, and offered on the form. */
export const INVYO_EXTENSION_DATA_SOURCE: DataSourceCategory = "invyo_extension";

/** The key of the "Other" entry, as a category and as a tool. */
export const DATA_SOURCE_OTHER = "other";

export const DATA_SOURCE_CATEGORY_LABELS: Record<DataSourceCategory, string> = {
    crm: "CRM",
    sales_prospecting_tool: "Sales Prospecting Tool",
    invyo_extension: "Invyo Extension",
    other: "Other",
};

export interface DataSourceTool {
    key: string;
    label: string;
}

/**
 * The tools a category offers. A category absent from here (Invyo Extension,
 * Other) takes no tool.
 */
export const DATA_SOURCE_TOOLS: Partial<Record<DataSourceCategory, readonly DataSourceTool[]>> = {
    crm: [
        { key: "salesforce", label: "Salesforce" },
        { key: "hubspot", label: "HubSpot" },
        { key: "pipedrive", label: "Pipedrive" },
        { key: "attio", label: "Attio" },
        { key: "affinity", label: "Affinity" },
        { key: DATA_SOURCE_OTHER, label: "Other" },
    ],
    sales_prospecting_tool: [
        { key: "apollo", label: "Apollo.io" },
        { key: "lusha", label: "Lusha" },
        { key: "kaspr", label: "Kaspr" },
        { key: "cognism", label: "Cognism" },
        { key: "zoominfo", label: "ZoomInfo" },
        { key: "dealfront", label: "Dealfront" },
        { key: "linkedin_sales_navigator", label: "LinkedIn Sales Navigator" },
        { key: "clay", label: "Clay" },
        { key: DATA_SOURCE_OTHER, label: "Other" },
    ],
};

/** The free text's maximum length, the same as a list name's. */
export const DATA_SOURCE_DETAIL_MAX_LENGTH = 100;

export function isDataSourceCategory(value: unknown): value is DataSourceCategory {
    return typeof value === "string" && (DATA_SOURCE_CATEGORIES as readonly string[]).includes(value);
}

/** The tools of a category; empty for a category that takes none. */
export function dataSourceToolsOf(category: DataSourceCategory): readonly DataSourceTool[] {
    return DATA_SOURCE_TOOLS[category] ?? [];
}

export function categoryTakesTool(category: DataSourceCategory): boolean {
    return dataSourceToolsOf(category).length > 0;
}

export function isToolOfCategory(category: DataSourceCategory, tool: string): boolean {
    return dataSourceToolsOf(category).some((t) => t.key === tool);
}

/**
 * Whether the free text is required: for the "Other" category, and for the
 * "Other" tool of a category that has tools.
 */
export function dataSourceNeedsDetail(category: DataSourceCategory, tool: string | null): boolean {
    if (category === DATA_SOURCE_OTHER) return true;
    return categoryTakesTool(category) && tool === DATA_SOURCE_OTHER;
}

/**
 * A data source as the client import sends it (`data_source` of
 * `POST /csv/import`). The text is trimmed; a text of spaces only counts as
 * missing. The output is normalized: `tool` is null for a category without
 * tools, `detail` is null unless the pair requires it.
 */
export const ListDataSourceSchema = z
    .object(
        {
            category: z.enum(DATA_SOURCE_CATEGORIES, { error: "Unknown data source category." }),
            tool: z.string({ error: "The data source tool must be text." }).nullable().optional(),
            detail: z.string({ error: "The data source description must be text." }).nullable().optional(),
        },
        { error: "data_source must be an object with a category." },
    )
    .transform((value, ctx) => {
        const { category } = value;
        const tool = value.tool ?? null;
        const detail = value.detail?.trim() ?? "";

        if (categoryTakesTool(category)) {
            if (!tool) {
                ctx.addIssue({ code: "custom", path: ["tool"], message: "Please choose a tool for this data source." });
                return z.NEVER;
            }
            if (!isToolOfCategory(category, tool)) {
                ctx.addIssue({ code: "custom", path: ["tool"], message: "This tool is not offered for this data source category." });
                return z.NEVER;
            }
        } else if (tool) {
            ctx.addIssue({ code: "custom", path: ["tool"], message: "This data source category takes no tool." });
            return z.NEVER;
        }

        const needsDetail = dataSourceNeedsDetail(category, tool);
        if (needsDetail && detail === "") {
            ctx.addIssue({ code: "custom", path: ["detail"], message: "Please describe the data source." });
            return z.NEVER;
        }
        if (needsDetail && detail.length > DATA_SOURCE_DETAIL_MAX_LENGTH) {
            ctx.addIssue({
                code: "custom",
                path: ["detail"],
                message: `The data source must be at most ${DATA_SOURCE_DETAIL_MAX_LENGTH} characters.`,
            });
            return z.NEVER;
        }

        return {
            category,
            tool: categoryTakesTool(category) ? tool : null,
            detail: needsDetail ? detail : null,
        };
    });
export type ListDataSourceInput = z.input<typeof ListDataSourceSchema>;
export type ListDataSource = z.output<typeof ListDataSourceSchema>;

/**
 * `GET /lists/import-data-source`: the data source of the licence's last real
 * file import, to pre-fill New Import (#749). Plain strings on purpose: a key
 * that left the constants since is dropped by the form, not refused here.
 */
export const ImportDataSourcePrefillSchema = z
    .object({
        category: z.string(),
        tool: z.string().nullable(),
        detail: z.string().nullable(),
    })
    .nullable();
export type ImportDataSourcePrefill = z.infer<typeof ImportDataSourcePrefillSchema>;

/**
 * The Collection column (#749): "CRM · HubSpot", "Other · Excel export",
 * "CRM · Other (Zoho)", "Invyo Extension"; "-" for a list without a data
 * source. A key unknown to the constants is shown as stored.
 */
export function formatDataSource(
    category: string | null | undefined,
    tool: string | null | undefined,
    detail: string | null | undefined,
): string {
    if (!category) return "-";
    const categoryLabel = isDataSourceCategory(category) ? DATA_SOURCE_CATEGORY_LABELS[category] : category;
    const text = detail?.trim() ?? "";

    if (tool) {
        const known = isDataSourceCategory(category)
            ? dataSourceToolsOf(category).find((t) => t.key === tool)
            : undefined;
        const toolLabel = known?.label ?? tool;
        const toolPart = tool === DATA_SOURCE_OTHER && text ? `${toolLabel} (${text})` : toolLabel;
        return `${categoryLabel} · ${toolPart}`;
    }
    if (text) return `${categoryLabel} · ${text}`;
    return categoryLabel;
}
